import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { settingsAPI } from '../api/settings.api';

const SiteSettingsContext = createContext(null);

function applyTheme(theme = {}) {
  const root = document.documentElement;
  const map = {
    '--color-primary': theme.primary,
    '--color-primary-hover': theme.primaryHover,
    '--color-text': theme.text,
    '--color-muted': theme.mutedText,
    '--color-surface': theme.surface,
    '--color-background': theme.background,
    '--color-border': theme.border,
    '--color-header-bg': theme.headerBackground,
    '--color-header-text': theme.headerText,
    '--color-card': theme.card,
    '--color-section': theme.section,
    '--color-input-bg': theme.inputBackground,
    '--color-input-text': theme.inputText,
    '--color-button-text': theme.buttonText,
    '--color-hero-text': theme.heroText,
    '--color-hero-overlay': theme.heroOverlay,
    '--color-hero-button-bg': theme.heroButtonBg,
    '--color-hero-button-text': theme.heroButtonText,
  };

  Object.entries(map).forEach(([key, value]) => {
    if (value) root.style.setProperty(key, value);
  });
}

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await settingsAPI.getPublic();
        const loaded = data?.settings || null;
        setSettings(loaded);
        applyTheme(loaded?.theme || {});
      } catch (error) {
        console.error('Failed to load site settings', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const value = useMemo(
    () => ({
      settings,
      loading,
      reloadSettings: async () => {
        const data = await settingsAPI.getPublic();
        const loaded = data?.settings || null;
        setSettings(loaded);
        applyTheme(loaded?.theme || {});
        return loaded;
      },
    }),
    [settings, loading]
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  }
  return context;
};

export default SiteSettingsContext;
